import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { CurrencyPair, ExchangeRateData, ChartTimeframe } from '@/types'
import { exchangeRateService } from '@/services/exchangeRateService'

interface CachedData {
  data: ExchangeRateData[]
  timestamp: number
  timeframe: string
}

export const useExchangeRateStore = defineStore('exchangeRate', () => {
  // Currency pairs as defined in PRD
  const currencyPairs = ref<CurrencyPair[]>([
    { id: 'czk-btc', base: 'CZK', target: 'BTC', name: 'CZK to Bitcoin', type: 'crypto' },
    { id: 'czk-eth', base: 'CZK', target: 'ETH', name: 'CZK to Ethereum', type: 'crypto' },
    { id: 'czk-usd', base: 'CZK', target: 'USD', name: 'CZK to US Dollar', type: 'fiat' },
    { id: 'czk-eur', base: 'CZK', target: 'EUR', name: 'CZK to Euro', type: 'fiat' }
  ])

  // Time frame options as defined in PRD
  const timeframes = ref<ChartTimeframe[]>([
    { label: '1D', value: '1D', days: 1 },
    { label: '1W', value: '1W', days: 7 },
    { label: '1M', value: '1M', days: 30 },
    { label: '6M', value: '6M', days: 180 },
    { label: '1Y', value: '1Y', days: 365 }
  ])

  // Enhanced store with caching - stores CachedData objects
  const chartDataCache = ref<Record<string, Record<string, CachedData>>>({})
  const loading = ref<Record<string, boolean>>({})
  const errors = ref<Record<string, string>>({})

  // Cache duration: 5 minutes for crypto, 15 minutes for fiat
  const CACHE_DURATION = {
    crypto: 5 * 60 * 1000,  // 5 minutes
    fiat: 15 * 60 * 1000    // 15 minutes
  }

  // Check if cached data is still valid
  function isCacheValid(pairId: string, timeframe: string): boolean {
    const cached = chartDataCache.value[pairId]?.[timeframe]
    if (!cached) return false

    const pair = currencyPairs.value.find(p => p.id === pairId)
    if (!pair) return false

    const maxAge = CACHE_DURATION[pair.type]
    const now = Date.now()

    return (now - cached.timestamp) < maxAge
  }

  // localStorage caching enabled for crypto currencies only
  // Memory cache is active for all currencies for performance during the same session  // Load cached data from localStorage for crypto currencies
  // For crypto, we cache both 30-day and 365-day datasets
  function loadFromLocalStorage(pairId: string, days: number): CachedData | null {
    const pair = currencyPairs.value.find(p => p.id === pairId)
    if (!pair || pair.type !== 'crypto') return null

    try {
      // Use 30D cache for daily/weekly/monthly, 365D cache for 6M/1Y
      const cacheKey = days <= 30 ? '30D' : '365D'
      const key = `exchange_rate_${pairId}_${cacheKey}`
      const cached = localStorage.getItem(key)
      if (cached) {
        const parsedData: CachedData = JSON.parse(cached)

        // Check if cache is still valid
        const maxAge = CACHE_DURATION[pair.type]
        const now = Date.now()

        if ((now - parsedData.timestamp) < maxAge) {
          return parsedData
        } else {
          // Remove expired cache
          localStorage.removeItem(key)
        }
      }
    } catch (error) {
      console.error('Error loading from localStorage:', error)
    }

    return null
  }

  // Save data to localStorage for crypto currencies
  // Save both 30-day and 365-day datasets as appropriate
  function saveToLocalStorage(pairId: string, cachedData: CachedData, days: number) {
    const pair = currencyPairs.value.find(p => p.id === pairId)
    if (!pair || pair.type !== 'crypto') return

    try {
      // Use 30D cache for daily/weekly/monthly, 365D cache for 6M/1Y
      const cacheKey = days <= 30 ? '30D' : '365D'
      const key = `exchange_rate_${pairId}_${cacheKey}`
      localStorage.setItem(key, JSON.stringify(cachedData))
    } catch (error) {
      console.error('Error saving to localStorage:', error)
    }
  }

  // Filter cached data for shorter timeframes
  function filterDataForTimeframe(fullData: ExchangeRateData[], days: number): ExchangeRateData[] {
    if (days >= 365) {
      return fullData
    }

    // Calculate cutoff date for filtering
    const cutoffDate = new Date()

    // For 1-day charts, we want data from the last 24 hours
    if (days === 1) {
      cutoffDate.setHours(cutoffDate.getHours() - 24)
    } else {
      cutoffDate.setDate(cutoffDate.getDate() - days)
    }

    const cutoffISO = cutoffDate.toISOString()

    // Filter to only include data within the requested timeframe
    return fullData.filter(item => item.date >= cutoffISO)
  }

  // Calculate percentile value from an array of rates
  function calculatePercentile(rates: number[], percentile: number): number {
    if (rates.length === 0) return 0

    // Sort the array in ascending order
    const sortedRates = [...rates].sort((a, b) => a - b)

    // Calculate the index for the percentile
    const index = (percentile / 100) * (sortedRates.length - 1)

    // If index is a whole number, return that element
    if (Number.isInteger(index)) {
      return sortedRates[index]
    }

    // Otherwise, interpolate between the two nearest values
    const lowerIndex = Math.floor(index)
    const upperIndex = Math.ceil(index)
    const weight = index - lowerIndex

    return sortedRates[lowerIndex] * (1 - weight) + sortedRates[upperIndex] * weight
  }

  // Get 20th percentile values for 1 month and 6 months (values lower than 80% of prices)
  // This represents the price level below which 80% of all prices fall
  function getPercentileValues(pairId: string): { oneMonth: number; sixMonths: number } {
    // Get data from cache for different timeframes
    const chartData = chartDataCache.value[pairId]

    let oneMonthRates: number[] = []
    let sixMonthsRates: number[] = []

    // Try to get 1 month data directly
    if (chartData?.['1M']?.data) {
      oneMonthRates = chartData['1M'].data.map(item => item.rate)
    }

    // Try to get 6 months data directly
    if (chartData?.['6M']?.data) {
      sixMonthsRates = chartData['6M'].data.map(item => item.rate)
    }

    // If we don't have 1M data but have 6M data, derive 1M from 6M
    if (oneMonthRates.length === 0 && chartData?.['6M']?.data) {
      const oneMonthFiltered = filterDataForTimeframe(chartData['6M'].data, 30)
      oneMonthRates = oneMonthFiltered.map(item => item.rate)
    }

    // If we don't have 6M data but have 1Y data, derive 6M from 1Y
    if (sixMonthsRates.length === 0 && chartData?.['1Y']?.data) {
      const sixMonthsFiltered = filterDataForTimeframe(chartData['1Y'].data, 180)
      sixMonthsRates = sixMonthsFiltered.map(item => item.rate)
    }

    // If we still don't have 1M data but have longer timeframes, try to derive it
    if (oneMonthRates.length === 0) {
      // Try from any available longer timeframe data
      const availableData = chartData?.['1Y']?.data || chartData?.['6M']?.data
      if (availableData) {
        const oneMonthFiltered = filterDataForTimeframe(availableData, 30)
        oneMonthRates = oneMonthFiltered.map(item => item.rate)
      }
    }

    return {
      oneMonth: calculatePercentile(oneMonthRates, 20),
      sixMonths: calculatePercentile(sixMonthsRates, 20)
    }
  }  // Fetch exchange rate data for a specific currency pair and timeframe with caching
  async function fetchExchangeRateData(pairId: string, timeframe: string, forceRefresh = false) {
    const pair = currencyPairs.value.find(p => p.id === pairId)
    if (!pair) return

    const timeframeData = timeframes.value.find(t => t.value === timeframe)
    if (!timeframeData) return

    // Check cache first
    if (!forceRefresh) {
      // Check memory cache first
      if (isCacheValid(pairId, timeframe)) {
        console.log(`Using memory cached data for ${pairId} ${timeframe}`)
        return
      }      // For crypto currencies, check if we have appropriate cached data
      if (pair.type === 'crypto') {
        // Determine which dataset to use: 30-day for 1D/1W/1M, 365-day for 6M/1Y
        const useMonthlyData = timeframeData.days <= 30
        const sourceDays = useMonthlyData ? 30 : 365
        const sourceCacheKey = useMonthlyData ? '1M' : '1Y'

        // Check if we have the appropriate source dataset in memory cache first
        const sourceDataInMemory = chartDataCache.value[pairId]?.[sourceCacheKey]

        if (sourceDataInMemory && isCacheValid(pairId, sourceCacheKey)) {
          console.log(`Deriving ${timeframe} data from cached ${sourceCacheKey} dataset for ${pairId}`)
          const filteredData = filterDataForTimeframe(sourceDataInMemory.data, timeframeData.days)

          // Cache the derived data in memory
          if (!chartDataCache.value[pairId]) {
            chartDataCache.value[pairId] = {}
          }
          chartDataCache.value[pairId][timeframe] = {
            data: filteredData,
            timestamp: sourceDataInMemory.timestamp,
            timeframe
          }
          return
        }

        // Check localStorage for the appropriate dataset
        const localStorageData = loadFromLocalStorage(pairId, sourceDays)
        if (localStorageData) {
          console.log(`Loading ${sourceDays}-day data from localStorage and deriving ${timeframe} for ${pairId}`)

          // Store the source dataset in memory cache
          if (!chartDataCache.value[pairId]) {
            chartDataCache.value[pairId] = {}
          }
          chartDataCache.value[pairId][sourceCacheKey] = localStorageData

          // Derive and cache the requested timeframe
          const filteredData = filterDataForTimeframe(localStorageData.data, timeframeData.days)
          chartDataCache.value[pairId][timeframe] = {
            data: filteredData,
            timestamp: localStorageData.timestamp,
            timeframe
          }
          return
        }
      }
    }

    // Data not in cache or force refresh - fetch from API
    console.log(`Fetching fresh data for ${pairId} ${timeframe}`)
    loading.value[pairId] = true
    errors.value[pairId] = ''

    try {
      let data: ExchangeRateData[]

      if (pair.type === 'crypto') {
        const coinId = pair.target.toLowerCase() === 'btc' ? 'bitcoin' : 'ethereum'

        // Determine which dataset to fetch: 30-day for 1D/1W/1M, 365-day for 6M/1Y
        const useMonthlyData = timeframeData.days <= 30
        const fetchDays = useMonthlyData ? 30 : 365
        const sourceCacheKey = useMonthlyData ? '1M' : '1Y'

        // Fetch data from service
        data = await exchangeRateService.getCryptoHistoricalData(coinId, timeframeData.days)

        // Also fetch and cache the appropriate source dataset if we don't have it
        if (!chartDataCache.value[pairId]?.[sourceCacheKey] ||
            !isCacheValid(pairId, sourceCacheKey)) {
          try {
            const sourceData = await exchangeRateService.getCryptoHistoricalData(coinId, fetchDays)

            // Cache the source dataset
            const sourceCached: CachedData = {
              data: sourceData,
              timestamp: Date.now(),
              timeframe: sourceCacheKey
            }

            // Initialize nested object if needed
            if (!chartDataCache.value[pairId]) {
              chartDataCache.value[pairId] = {}
            }

            // Store source data in memory and localStorage
            chartDataCache.value[pairId][sourceCacheKey] = sourceCached
            saveToLocalStorage(pairId, sourceCached, fetchDays)

            console.log(`Cached ${fetchDays}-day dataset for future use: ${pairId}`)
          } catch (sourceError) {
            console.warn(`Could not fetch ${fetchDays}-day data for ${pairId}:`, sourceError)
          }
        }
      } else {
        data = await exchangeRateService.getFiatHistoricalData(pair.target, timeframeData.days)
      }

      // Create cached data object for the requested timeframe
      const cachedData: CachedData = {
        data,
        timestamp: Date.now(),
        timeframe
      }

      // Initialize nested object if needed
      if (!chartDataCache.value[pairId]) {
        chartDataCache.value[pairId] = {}
      }

      // Store in memory cache
      chartDataCache.value[pairId][timeframe] = cachedData

      // For crypto: save to localStorage if this was a source dataset request
      if (pair.type === 'crypto' && (timeframeData.days === 30 || timeframeData.days >= 365)) {
        saveToLocalStorage(pairId, cachedData, timeframeData.days)
        console.log(`Successfully cached ${timeframeData.days}-day data to localStorage for ${pairId}`)
      } else if (pair.type === 'crypto') {
        console.log(`Successfully cached ${timeframe} data to memory for ${pairId}`)
      } else {
        console.log(`Successfully cached data to memory for ${pairId} ${timeframe}`)
      }

    } catch (error) {
      errors.value[pairId] = error instanceof Error ? error.message : 'Failed to fetch data'
      console.error(`Error fetching data for ${pairId}:`, error)
    } finally {
      loading.value[pairId] = false
    }
  }

  // Get chart data for a specific pair and timeframe
  function getChartData(pairId: string, timeframe: string): ExchangeRateData[] {
    return chartDataCache.value[pairId]?.[timeframe]?.data || []
  }

  // Check if data is loading for a specific pair
  function isLoading(pairId: string): boolean {
    return loading.value[pairId] || false
  }

  // Get error for a specific pair
  function getError(pairId: string): string {
    return errors.value[pairId] || ''
  }

  // Clear error for a specific pair
  function clearError(pairId: string) {
    errors.value[pairId] = ''
  }

  // Force refresh data (bypasses cache)
  async function refreshData(pairId: string, timeframe: string) {
    await fetchExchangeRateData(pairId, timeframe, true)
  }

  // Get the current (most recent) price for a currency pair
  function getCurrentPrice(pairId: string): { currentPrice: number; priceChange: number; priceChangePercent: number } | null {
    // Try to get data from any available timeframe, preferring shorter timeframes for more recent data
    const timeframePreference = ['1D', '1W', '1M', '6M']

    for (const tf of timeframePreference) {
      const data = chartDataCache.value[pairId]?.[tf]?.data
      if (data && data.length > 0) {
        // Sort by date to ensure we get the most recent data
        const sortedData = [...data].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
        const currentPrice = sortedData[0].rate

        // Calculate price change if we have at least 2 data points
        let priceChange = 0
        let priceChangePercent = 0

        if (sortedData.length >= 2) {
          const previousPrice = sortedData[1].rate
          priceChange = currentPrice - previousPrice
          priceChangePercent = previousPrice > 0 ? (priceChange / previousPrice) * 100 : 0
        }

        return {
          currentPrice,
          priceChange,
          priceChangePercent
        }
      }
    }

    return null
  }

  // Calculate price differences for 6M, 1M, and 1W periods using 6M data
  function getPriceDiffLabels(pairId: string): {
    sixMonths: { diff: number; diffPercent: number } | null;
    oneMonth: { diff: number; diffPercent: number } | null;
    oneWeek: { diff: number; diffPercent: number } | null;
  } {
    // Try to get 6M data first, then fallback to longer timeframes
    const sixMonthData = chartDataCache.value[pairId]?.['6M']?.data ||
                        chartDataCache.value[pairId]?.['1Y']?.data

    if (!sixMonthData || sixMonthData.length === 0) {
      return {
        sixMonths: null,
        oneMonth: null,
        oneWeek: null
      }
    }

    // Sort data by date (newest first)
    const sortedData = [...sixMonthData].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())

    if (sortedData.length === 0) {
      return {
        sixMonths: null,
        oneMonth: null,
        oneWeek: null
      }
    }

    const currentPrice = sortedData[0].rate
    const currentDate = new Date(sortedData[0].date)

    // Calculate target dates
    const sixMonthsAgo = new Date(currentDate)
    sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6)

    const oneMonthAgo = new Date(currentDate)
    oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1)

    const oneWeekAgo = new Date(currentDate)
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7)

    // Find closest data points for each period
    function findClosestPrice(targetDate: Date): number | null {
      let closestData = sortedData[0]
      let smallestDiff = Math.abs(new Date(closestData.date).getTime() - targetDate.getTime())

      for (const dataPoint of sortedData) {
        const diff = Math.abs(new Date(dataPoint.date).getTime() - targetDate.getTime())
        if (diff < smallestDiff) {
          smallestDiff = diff
          closestData = dataPoint
        }
      }

      // Only return if we found data within reasonable range (e.g., within 3 days for weekly, 5 days for monthly, 10 days for 6M)
      const maxDiffDays = targetDate === oneWeekAgo ? 3 : targetDate === oneMonthAgo ? 5 : 10
      const maxDiffMs = maxDiffDays * 24 * 60 * 60 * 1000

      return smallestDiff <= maxDiffMs ? closestData.rate : null
    }

    const sixMonthsPrice = findClosestPrice(sixMonthsAgo)
    const oneMonthPrice = findClosestPrice(oneMonthAgo)
    const oneWeekPrice = findClosestPrice(oneWeekAgo)

    // Calculate differences
    function calculateDiff(historicalPrice: number | null): { diff: number; diffPercent: number } | null {
      if (historicalPrice === null) return null

      const diff = currentPrice - historicalPrice
      const diffPercent = historicalPrice > 0 ? (diff / historicalPrice) * 100 : 0

      return { diff, diffPercent }
    }

    return {
      sixMonths: calculateDiff(sixMonthsPrice),
      oneMonth: calculateDiff(oneMonthPrice),
      oneWeek: calculateDiff(oneWeekPrice)
    }
  }  // Clear all cached data (memory cache and localStorage for crypto)
  function clearCache() {
    chartDataCache.value = {}

    // Clear localStorage for crypto currencies (both 30D and 365D datasets)
    currencyPairs.value.forEach(pair => {
      if (pair.type === 'crypto') {
        const keys = [`exchange_rate_${pair.id}_30D`, `exchange_rate_${pair.id}_365D`]
        keys.forEach(key => {
          try {
            localStorage.removeItem(key)
          } catch (error) {
            console.error('Error clearing localStorage:', error)
          }
        })
      }
    })

    console.log('Memory cache and localStorage (crypto) cleared successfully')
  }

  // Get cache status for debugging
  const cacheStatus = computed(() => {
    const status: Record<string, Record<string, { cached: boolean, age: number }>> = {}

    currencyPairs.value.forEach(pair => {
      status[pair.id] = {}
      timeframes.value.forEach(tf => {
        const cached = chartDataCache.value[pair.id]?.[tf.value]
        if (cached) {
          status[pair.id][tf.value] = {
            cached: true,
            age: Date.now() - cached.timestamp
          }
        } else {
          status[pair.id][tf.value] = {
            cached: false,
            age: 0
          }
        }
      })
    })

    return status
  })

  return {
    currencyPairs,
    timeframes,
    fetchExchangeRateData,
    getChartData,
    isLoading,
    getError,
    clearError,
    refreshData,
    getCurrentPrice,
    getPriceDiffLabels,
    clearCache,
    cacheStatus,
    getPercentileValues: getPercentileValues
  }
})
